import {
    addAppBreadcrumb,
    captureAppException,
    captureAppMessage,
    SENTRY_SECTION,
} from '../utils/sentry';

/** RTK slice adı → GlitchTip `section` etiketi */
const SLICE_TO_SECTION = {
    userProfile: SENTRY_SECTION.auth,
    callCenter: SENTRY_SECTION.callcenter,
    supplier: SENTRY_SECTION.supplier,
    notification: SENTRY_SECTION.notification,
    util: SENTRY_SECTION.util,
    industry: SENTRY_SECTION.industry,
    file: SENTRY_SECTION.file,
    supplierApp: SENTRY_SECTION.supplier,
    supplierFile: SENTRY_SECTION.file,
    customizer: SENTRY_SECTION.general,
};

const thunkNameToSnake = (name) =>
    String(name || '')
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase()
        .replace(/^_/, '');

const summarizePayload = (payload) => {
    if (payload == null) return null;
    if (typeof payload === 'string') return payload.length > 240 ? `${payload.slice(0, 240)}…` : payload;
    if (typeof payload === 'number' || typeof payload === 'boolean') return payload;
    try {
        const s = JSON.stringify(payload);
        return s.length > 320 ? `${s.slice(0, 320)}…` : s;
    } catch {
        return '[unserializable]';
    }
};

const parseThunkType = (type) => {
    if (!type || typeof type !== 'string' || !type.endsWith('/rejected')) return null;
    const base = type.slice(0, -'/rejected'.length);
    const slash = base.indexOf('/');
    if (slash < 0) return null;
    return {
        slice: base.slice(0, slash),
        thunk: base.slice(slash + 1),
        full: base,
    };
};

const getTagsForThunk = (parsed) => {
    if (!parsed) {
        return {
            section: SENTRY_SECTION.general,
            action: 'unknown',
            redux_slice: 'unknown',
            redux_thunk: 'unknown',
        };
    }
    const section = SLICE_TO_SECTION[parsed.slice] || SENTRY_SECTION.general;
    const action = thunkNameToSnake(parsed.thunk) || 'unknown';
    return {
        section,
        action,
        redux_slice: parsed.slice,
        redux_thunk: parsed.thunk,
    };
};

/**
 * Tüm async thunk reddleri: breadcrumb (Logs / issue bağlamı).
 * Sadece gerçek throw (!rejectedWithValue): Issues’a exception.
 * rejectWithValue: isteğe bağlı info mesajı (varsayılan kapalı — gürültü).
 */
export const sentryReduxMiddleware = (_store) => (next) => (action) => {
    const result = next(action);

    if (!action?.type?.endsWith?.('/rejected')) return result;

    const mode = String(import.meta.env.VITE_SENTRY_REDUX_LOG || 'errors_and_breadcrumbs').toLowerCase();
    const logRejectedValues = mode === 'all';

    const parsed = parseThunkType(action.type);
    const tags = getTagsForThunk(parsed);

    addAppBreadcrumb({
        category: 'redux.async',
        type: 'default',
        level: action.meta?.rejectedWithValue ? 'warning' : 'error',
        message: parsed?.full || action.type,
        data: {
            rejectedWithValue: Boolean(action.meta?.rejectedWithValue),
            payload: summarizePayload(action.payload),
        },
    });

    if (!action.meta?.rejectedWithValue && action.error) {
        const err = new Error(action.error.message || 'Async thunk failed');
        if (action.error.stack) err.stack = action.error.stack;
        captureAppException(err, {
            tags,
            extra: { thunk: action.type },
        });
    } else if (logRejectedValues && action.meta?.rejectedWithValue) {
        captureAppMessage(`redux.rejected ${parsed?.full || action.type}`, 'info', {
            tags: {
                ...tags,
                layer: 'domain',
                event_type: 'log',
            },
            extra: {
                thunk: action.type,
                payload: summarizePayload(action.payload),
            },
        });
    }

    return result;
};
